import type { HandbookBlock, HandbookChapter, HandbookSection } from './model';

/**
 * Die Suche im Handbuch.
 *
 * **Warum keine Bibliothek.** Fuenfundzwanzig Kapitel sind kein Bestand,
 * der einen Index rechtfertigt. Ein Volltextvergleich ueber ein paar
 * hunderttausend Zeichen laeuft im Browser unter einer Millisekunde -- eine
 * Abhaengigkeit dafuer waere Aufwand ohne Nutzen (docs/04, Abschnitt 11).
 *
 * **Warum ohne Wortstaemme.** Anders als die Wissensdatenbank, die gegen
 * PostgreSQL sucht, hat das Handbuch keinen Server hinter sich. Statt eine
 * halbe Stammformreduktion nachzubauen, sucht es nach Teilzeichenfolgen --
 * "kategor" findet "Kategorie" und "Kategorien". Das deckt den haeufigen
 * Fall ab, ohne so zu tun, als koenne es mehr.
 */

/** Alle durchsuchbaren Zeichenketten eines Blocks. */
function textOf(block: HandbookBlock): string[] {
  switch (block.kind) {
    case 'text':
      return [block.text];
    case 'steps':
      return block.steps.flatMap((step) => [step.text, step.detail ?? '']);
    case 'note':
      return [block.title, block.text];
    case 'shot':
      return [block.caption];
    case 'table':
      return [...block.head, ...block.rows.flat()];
    case 'terms':
      return block.items.flatMap((item) => [item.term, item.text]);
    case 'route':
      return [block.label, block.hint ?? ''];
    case 'example':
      return [block.title, block.text];
  }
}

function sectionText(section: HandbookSection): string {
  return [section.title, ...section.blocks.flatMap(textOf)].join(' ');
}

function chapterText(chapter: HandbookChapter): string {
  return [chapter.title, chapter.summary, ...chapter.sections.map(sectionText)].join(' ');
}

export interface HandbookHit {
  chapter: HandbookChapter;
  /** Die Abschnitte, in denen der Begriff steht. Leer: nur der Kapitelkopf trifft. */
  sections: HandbookSection[];
}

/**
 * Sucht ueber alle Kapitel.
 *
 * Mehrere Woerter werden mit "und" verknuepft und nicht als Wendung gesucht:
 * Wer "sla kalender" eingibt, sucht die Stelle, an der beides vorkommt, und
 * nicht die Zeichenfolge "sla kalender".
 */
export function searchHandbook(chapters: HandbookChapter[], query: string): HandbookHit[] {
  const needles = query
    .toLocaleLowerCase('de')
    .split(/\s+/)
    .map((word) => word.trim())
    .filter(Boolean);

  if (needles.length === 0) return chapters.map((chapter) => ({ chapter, sections: [] }));

  const hits: HandbookHit[] = [];

  for (const chapter of chapters) {
    const haystack = chapterText(chapter).toLocaleLowerCase('de');
    if (!needles.every((needle) => haystack.includes(needle))) continue;

    const sections = chapter.sections.filter((section) => {
      const text = sectionText(section).toLocaleLowerCase('de');
      return needles.every((needle) => text.includes(needle));
    });

    hits.push({ chapter, sections });
  }

  return hits;
}
